import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { AbsenceCategoryEnum, AbsencesService } from '@wilson/absences';
import { Absence, AbsenceEntity, AbsenceStates } from '@wilson/interfaces';
import { dateToPositionPx } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  getDateFromPixelPosition,
  HorizontalDemarcatorRange,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineState,
  TimelineRemoveAbsence,
  TimelineStaysSuggestionService,
  TimelineUpsertNewAbsence,
} from '@wilson/shift-timeline/state';
import { addHours, endOfDay, startOfDay, startOfMinute } from 'date-fns';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateService } from '@ngx-translate/core';

const ABSENCE_DURATION_HOURS = 12;

@Injectable()
export class NewAbsenceDropZoneService {
  private store = inject(Store);
  private readonly absencesService = inject(AbsencesService);
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );
  private readonly messageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);

  handleOnDragOverProcess({
    offsetX,
    userId,
    absenceDurationHours,
    isFullDay,
  }: {
    offsetX: number;
    userId: string | null | undefined;
    absenceDurationHours?: number;
    isFullDay: boolean;
  }): {
    currentDemarcatorRange: HorizontalDemarcatorRange;
    mockAbsence: undefined | (AbsenceEntity & { id: string });
  } | null {
    if (userId) {
      let absenceStartDate = getDateFromPixelPosition({
        pixelPosition: offsetX,
        timeframe: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        frameLength: this.store.selectSnapshot(ShiftTimelineState.frameLength),
      });
      let absenceEndDate = startOfMinute(
        addHours(
          absenceStartDate,
          absenceDurationHours || ABSENCE_DURATION_HOURS,
        ),
      );

      const currentDemarcatorRange = {
        left: offsetX,
        right: dateToPositionPx(absenceEndDate, {
          fromRightSide: true,
          timelineWidthPixel: this.store.selectSnapshot(
            ShiftTimelineState.frameLength,
          ),
          interval: this.store.selectSnapshot(ShiftTimelineDataState.timeframe),
        }),
        shiftDuration: null,
        adjustmentNeeded: true,
      };

      if (isFullDay) {
        absenceStartDate = startOfDay(absenceStartDate);
        absenceEndDate = endOfDay(absenceStartDate);
      }

      const mockAbsence = this.createMockAbsence(
        absenceStartDate,
        absenceEndDate,
        userId,
      );
      this.store.dispatch(
        new TimelineUpsertNewAbsence({
          absence: mockAbsence,
        }),
      );

      this.dragAndDropModeService.setDisplayMovingDemarcatorLine(true);
      this.dragAndDropModeService.setHorizontalDemarcatorRange(
        currentDemarcatorRange,
      );

      return {
        mockAbsence,
        currentDemarcatorRange,
      };
    } else {
      return null;
    }
  }

  async handleOnDropProcess({
    currentDemarcatorRange,
    mockAbsence,
    userId,
  }: {
    currentDemarcatorRange: HorizontalDemarcatorRange | null;
    mockAbsence: undefined | (AbsenceEntity & { id: string });
    userId: string;
  }): Promise<void> {
    if (mockAbsence && userId) {
      this.createNewViaDragAndDropService.addUpdatingId(
        CreateNewViaDragAndDropService.MOCK_ABSENCE_ID,
      );
      try {
        // eslint-disable-next-line
        const { id, ...rest } = mockAbsence;

        const materializedAbsences =
          await this.absencesService.createAbsence(rest);

        const materializedAbsence = materializedAbsences[0];

        this.store.dispatch([
          new TimelineRemoveAbsence({
            absenceId: CreateNewViaDragAndDropService.MOCK_ABSENCE_ID,
            userId: userId,
          }),
          new TimelineUpsertNewAbsence({
            absence: materializedAbsence as Absence & {
              id: string;
              isHighlighted: boolean;
            },
          }),
        ]);

        this.timelineCommonOperationsService.updateUsersAssignmentsState([
          materializedAbsence.userId,
        ]);
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          materializedAbsence.userId,
        ]);
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          materializedAbsence.userId,
        ]);

        this.timelineCommonOperationsService.openAbsenceDrawer(
          currentDemarcatorRange?.left || null,
          currentDemarcatorRange?.right || null,
          materializedAbsence.id,
        );
      } catch {
        this.messageService.error(this.translate.instant('general.error'));
      } finally {
        this.createNewViaDragAndDropService.removeUpdatingId(
          CreateNewViaDragAndDropService.MOCK_ABSENCE_ID,
        );
      }
    }
  }

  private createMockAbsence(
    absenceStartDate: Date,
    absenceEndDate: Date,
    userId: string,
  ): AbsenceEntity & { id: string; isHighlighted: boolean } {
    const mockAbsence: AbsenceEntity & { id: string; isHighlighted: boolean } =
      {
        id: CreateNewViaDragAndDropService.MOCK_ABSENCE_ID,
        status: AbsenceStates.accepted,
        userId: userId,
        absentFrom: absenceStartDate.toISOString(),
        absentTo: absenceEndDate.toISOString(),
        absenceCategoryId: AbsenceCategoryEnum.RestDay,
        isHighlighted: true,
      };
    return mockAbsence;
  }
}
